import clsx from 'clsx';
import { usePathname, useRouter } from 'next/navigation';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from 'components/ui/form';
import { Slider } from 'components/ui/slider';
import { Switch } from 'components/ui/switch';

import { graphqlClient } from '@/lib/backend';
import { StateContext } from '@/lib/StateProvider';
import updateYAxis from '@/lib/updateYAxis';

import FormFieldColorPicker from '@/components/FormFieldColorPicker';

const COLORS = [
  '#2c98e2',
  '#dd3930',
  '#f8a900',
  '#6a4c93',
  '#3c6e71',
  '#000000',
];

type YAxisFormInputs = {
  fieldOrder: number;
};

type YAxisStyleFormInputs = {
  glow: boolean;
  showPoints: boolean;
  strokeColor: string;
  strokeOpacity: number;
  strokeWidth: number;
  fillColor: string;
  fillOpacity: number;
  labelFontSize: number;
  labelLength: number;
};

export function YAxisLabelPopupContents({
  className,
  session,
  yAxis,
  yAxisOriginalName,
  yAxisLabelsRenderer,
  setPreventPopoupHide,
  story,
  dataset,
}) {
  const router = useRouter();
  const { dispatch } = useContext(StateContext);
  const pathname = usePathname();
  const graphql = graphqlClient(session);

  const yAxisForm = useForm<YAxisFormInputs>();
  const yAxisStyleForm = useForm<YAxisStyleFormInputs>();

  return (
    <div
      className={clsx([
        // NOTE: "p-4" is synced to event component.
        'flex flex-col gap-8 p-2 !text-[8px] ![line-height:10px] sm:p-4 sm:!text-base',
        className,
      ])}
    >
      {(() => {
        if (story.getIn(['chart', 'timeSeriesProperties', 'mark']) === 'line') {
          return null;
        }

        return (
          <Form {...yAxisForm}>
            <FormField
              control={yAxisForm.control}
              name="fieldOrder"
              defaultValue={yAxis.get('fieldOrder')}
              render={({ field }) => {
                return (
                  <FormItem className="grid grid-cols-2 items-center">
                    <div className="col-span-2 grid grid-cols-2 items-center">
                      <FormLabel className="col-span-1 text-xs sm:text-base">
                        Field order
                      </FormLabel>

                      <div className="col-span-1 place-self-end text-xs sm:text-base">
                        {field.value}
                      </div>
                    </div>

                    <FormControl className="col-span-2">
                      <Slider
                        defaultValue={[yAxis.get('fieldOrder')]}
                        max={50}
                        min={0}
                        step={1}
                        onValueChange={(values) => field.onChange(values)}
                        onValueCommit={(values) => {
                          updateYAxis(
                            session,
                            graphql,
                            pathname,
                            dispatch,
                            router,
                            yAxisLabelsRenderer,
                            story,
                            dataset,
                            yAxisOriginalName,
                            {
                              field_order: values[0],
                            },
                            {
                              shouldRefreshRouter: true,
                            }
                          );
                        }}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
          </Form>
        );
      })()}

      <Form {...yAxisStyleForm}>
        <form className="flex flex-col gap-8">
          {(() => {
            if (
              !(
                story.getIn(['chart', 'timeSeriesProperties', 'mark']) ===
                  'line' ||
                story.getIn(['chart', 'timeSeriesProperties', 'mark']) ===
                  'area'
              )
            ) {
              return null;
            }

            return (
              <FormField
                control={yAxisStyleForm.control}
                name="showPoints"
                defaultValue={yAxis.getIn(['style', 'showPoints'])}
                render={({ field }) => {
                  return (
                    <FormItem className="grid grid-cols-2 items-center gap-2">
                      <FormLabel className="col-span-2 text-xs sm:text-base">
                        Show points?
                      </FormLabel>

                      <FormControl className="col-span-2">
                        <Switch
                          className="!my-0"
                          checked={field.value}
                          onCheckedChange={(isChecked) => {
                            field.onChange(isChecked);

                            updateYAxis(
                              session,
                              graphql,
                              pathname,
                              dispatch,
                              router,
                              yAxisLabelsRenderer,
                              story,
                              dataset,
                              yAxisOriginalName,
                              {
                                style_show_points: isChecked,
                              },
                              {
                                shouldRefreshRouter: true,
                              }
                            );
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  );
                }}
              />
            );
          })()}

          <FormField
            control={yAxisStyleForm.control}
            name="glow"
            defaultValue={yAxis.getIn(['style', 'glow'])}
            render={({ field }) => {
              return (
                <FormItem className="grid grid-cols-2 items-center gap-2">
                  <FormLabel className="col-span-2 text-xs sm:text-base">
                    Glow?
                  </FormLabel>

                  <FormControl className="col-span-2">
                    <Switch
                      className="!my-0"
                      checked={field.value}
                      onCheckedChange={(isChecked) => {
                        field.onChange(isChecked);

                        updateYAxis(
                          session,
                          graphql,
                          pathname,
                          dispatch,
                          router,
                          yAxisLabelsRenderer,
                          story,
                          dataset,
                          yAxisOriginalName,
                          {
                            style_glow: isChecked,
                          },
                          {
                            shouldRefreshRouter: true,
                          }
                        );
                      }}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={yAxisStyleForm.control}
            name="labelFontSize"
            defaultValue={yAxis.getIn(['style', 'labelFontSize'])}
            render={({ field }) => {
              return (
                <FormItem className="grid grid-cols-2 items-center">
                  <div className="col-span-2 grid grid-cols-2 items-center">
                    <FormLabel className="col-span-1 text-xs sm:text-base">
                      Font size
                    </FormLabel>

                    <div className="col-span-1 place-self-end text-xs sm:text-base">
                      {field.value}
                    </div>
                  </div>

                  <FormControl className="col-span-2">
                    <Slider
                      defaultValue={[yAxis.getIn(['style', 'labelFontSize'])]}
                      max={50}
                      min={4}
                      step={1}
                      onValueChange={(values) => field.onChange(values)}
                      onValueCommit={(values) => {
                        updateYAxis(
                          session,
                          graphql,
                          pathname,
                          dispatch,
                          router,
                          yAxisLabelsRenderer,
                          story,
                          dataset,
                          yAxisOriginalName,
                          {
                            style_label_font_size: values[0],
                          },
                          {
                            shouldRefreshRouter: true,
                          }
                        );
                      }}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={yAxisStyleForm.control}
            name="labelLength"
            defaultValue={yAxis.getIn(['style', 'labelLength'])}
            render={({ field }) => {
              return (
                <FormItem className="grid grid-cols-2 items-center">
                  <div className="col-span-2 grid grid-cols-2 items-center">
                    <FormLabel className="col-span-1 text-xs sm:text-base">
                      Text length
                    </FormLabel>

                    <div className="col-span-1 place-self-end text-xs sm:text-base">
                      {field.value}
                    </div>
                  </div>

                  <FormControl className="col-span-2">
                    <Slider
                      defaultValue={[yAxis.getIn(['style', 'labelLength'])]}
                      max={100}
                      min={5}
                      step={5}
                      onValueChange={(values) => field.onChange(values)}
                      onValueCommit={(values) => {
                        updateYAxis(
                          session,
                          graphql,
                          pathname,
                          dispatch,
                          router,
                          yAxisLabelsRenderer,
                          story,
                          dataset,
                          yAxisOriginalName,
                          {
                            style_label_length: values[0],
                          },
                          {
                            shouldRefreshRouter: true,
                          }
                        );
                      }}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={yAxisStyleForm.control}
            name="strokeWidth"
            defaultValue={yAxis.getIn(['style', 'strokeWidth'])}
            render={({ field }) => {
              return (
                <FormItem className="grid grid-cols-2 items-center">
                  <div className="col-span-2 grid grid-cols-2 items-center">
                    <FormLabel className="col-span-1 text-xs sm:text-base">
                      Stroke width
                    </FormLabel>

                    <div className="col-span-1 place-self-end text-xs sm:text-base">
                      {field.value}
                    </div>
                  </div>

                  <FormControl className="col-span-2">
                    <Slider
                      defaultValue={[yAxis.getIn(['style', 'strokeWidth'])]}
                      max={10}
                      min={1}
                      step={1}
                      onValueChange={(values) => field.onChange(values)}
                      onValueCommit={(values) => {
                        updateYAxis(
                          session,
                          graphql,
                          pathname,
                          dispatch,
                          router,
                          yAxisLabelsRenderer,
                          story,
                          dataset,
                          yAxisOriginalName,
                          {
                            style_stroke_width: values[0],
                          },
                          {
                            shouldRefreshRouter: true,
                          }
                        );
                      }}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={yAxisStyleForm.control}
            name="strokeOpacity"
            defaultValue={yAxis.getIn(['style', 'strokeOpacity'])}
            render={({ field }) => {
              return (
                <FormItem className="grid grid-cols-2 items-center">
                  <div className="col-span-2 grid grid-cols-2 items-center">
                    <FormLabel className="col-span-1 text-xs sm:text-base">
                      Stroke opacity
                    </FormLabel>

                    <div className="col-span-1 place-self-end text-xs sm:text-base">
                      {field.value}
                    </div>
                  </div>

                  <FormControl className="col-span-2">
                    <Slider
                      defaultValue={[yAxis.getIn(['style', 'strokeOpacity'])]}
                      max={1}
                      min={0}
                      step={0.05}
                      onValueChange={(values) => field.onChange(values)}
                      onValueCommit={(values) => {
                        updateYAxis(
                          session,
                          graphql,
                          pathname,
                          dispatch,
                          router,
                          yAxisLabelsRenderer,
                          story,
                          dataset,
                          yAxisOriginalName,
                          {
                            style_stroke_opacity: values[0],
                          },
                          {
                            shouldRefreshRouter: true,
                          }
                        );
                      }}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormFieldColorPicker
            form={yAxisStyleForm}
            defaultValue={yAxis.getIn(['style', 'strokeColor'])}
            fieldName="strokeColor"
            labelText="Stroke color"
            presetColors={COLORS}
            onPopupOpenOrClose={(isOpen) => setPreventPopoupHide(isOpen)}
            onColorPicked={(color) => {
              updateYAxis(
                session,
                graphql,
                pathname,
                dispatch,
                router,
                yAxisLabelsRenderer,
                story,
                dataset,
                yAxisOriginalName,
                {
                  style_stroke_color: color,
                },
                { skipRedraw: true, shouldRefreshRouter: true }
              );
            }}
          />

          {(() => {
            if (
              story.getIn(['chart', 'timeSeriesProperties', 'mark']) === 'line'
            ) {
              return null;
            }

            return (
              <FormField
                control={yAxisStyleForm.control}
                name="fillOpacity"
                defaultValue={yAxis.getIn(['style', 'fillOpacity'])}
                render={({ field }) => {
                  return (
                    <FormItem className="grid grid-cols-2 items-center">
                      <div className="col-span-2 grid grid-cols-2 items-center">
                        <FormLabel className="col-span-1 text-xs sm:text-base">
                          Fill opacity
                        </FormLabel>

                        <div className="col-span-1 place-self-end text-xs sm:text-base">
                          {field.value}
                        </div>
                      </div>

                      <FormControl className="col-span-2">
                        <Slider
                          defaultValue={[yAxis.getIn(['style', 'fillOpacity'])]}
                          max={1}
                          min={0}
                          step={0.05}
                          onValueChange={(values) => field.onChange(values)}
                          onValueCommit={(values) => {
                            updateYAxis(
                              session,
                              graphql,
                              pathname,
                              dispatch,
                              router,
                              yAxisLabelsRenderer,
                              story,
                              dataset,
                              yAxisOriginalName,
                              {
                                style_fill_opacity: values[0],
                              },
                              {
                                shouldRefreshRouter: true,
                              }
                            );
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  );
                }}
              />
            );
          })()}

          {(() => {
            if (
              story.getIn(['chart', 'timeSeriesProperties', 'mark']) === 'line'
            ) {
              return null;
            }

            return (
              <FormFieldColorPicker
                form={yAxisStyleForm}
                defaultValue={yAxis.getIn(['style', 'fillColor'])}
                fieldName="fillColor"
                labelText="Fill color"
                presetColors={COLORS}
                onPopupOpenOrClose={(isOpen) => setPreventPopoupHide(isOpen)}
                onColorPicked={(color) => {
                  updateYAxis(
                    session,
                    graphql,
                    pathname,
                    dispatch,
                    router,
                    yAxisLabelsRenderer,
                    story,
                    dataset,
                    yAxisOriginalName,
                    {
                      style_fill_color: color,
                    },
                    { skipRedraw: true, shouldRefreshRouter: true }
                  );
                }}
              />
            );
          })()}
        </form>
      </Form>
    </div>
  );
}

export default YAxisLabelPopupContents;
